import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star, Send, Loader2, Check, MessageSquare } from 'lucide-react';

const TestimonialSection = () => {
  const { api, user } = useAuth();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [myTestimonial, setMyTestimonial] = useState(null);
  const [fetchingMy, setFetchingMy] = useState(true);
  
  const maxLength = 200;
  const userPlan = user?.plan || 'free';
  const canSubmit = userPlan !== 'free';
  
  useEffect(() => {
    fetchMyTestimonial();
  }, []);
  
  const fetchMyTestimonial = async () => {
    try {
      const response = await api.get('/testimonials/my');
      setMyTestimonial(response.data.testimonial);
    } catch (error) {
      console.error('Failed to fetch testimonial:', error);
    } finally {
      setFetchingMy(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    
    if (text.length > maxLength) {
      toast.error(`Feedback must be ${maxLength} characters or less`);
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/testimonials/submit', {
        rating,
        text: text.trim()
      });
      toast.success('Thank you! Your feedback is pending approval.');
      setMyTestimonial({ rating, text: text.trim(), status: 'pending' });
      setText('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit feedback';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  // Star rating component
  const StarRating = ({ value, onChange, readonly = false }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && onChange?.(star)}
          className={`transition-all duration-100 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          disabled={readonly}
        >
          <Star 
            className={`w-5 h-5 ${star <= value 
              ? 'fill-amber-400 text-amber-400' 
              : 'text-muted-foreground'
            }`}
          />
        </button>
      ))}
    </div>
  );
  
  if (fetchingMy) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  
  // Already submitted testimonial
  if (myTestimonial) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessageSquare className="w-5 h-5 text-primary" />
            Your Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/30 rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between mb-3">
              <StarRating value={myTestimonial.rating} readonly />
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                myTestimonial.status === 'approved' 
                  ? 'bg-green-500/10 text-green-600' 
                  : myTestimonial.status === 'rejected'
                  ? 'bg-red-500/10 text-red-600'
                  : 'bg-amber-500/10 text-amber-600'
              }`}>
                {myTestimonial.status === 'approved' ? 'Published' : 
                 myTestimonial.status === 'rejected' ? 'Not Approved' : 'Pending Review'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              "{myTestimonial.text}"
            </p>
          </div>
          
          {myTestimonial.status === 'approved' && (
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" />
              Your testimonial is visible on our homepage
            </p>
          )}
        </CardContent>
      </Card>
    );
  }
  
  // Free users can't submit
  if (!canSubmit) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center">
          <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Leave Feedback
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Subscribe to a plan to share your experience with MockupPro
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Submit form for subscribed users
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <MessageSquare className="w-5 h-5 text-primary" />
          Leave Feedback
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Rating */}
        <div>
          <label className="text-sm font-medium mb-2 block">Your Rating</label>
          <StarRating value={rating} onChange={setRating} />
        </div>
        
        {/* Testimonial text */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">Your Experience</label>
            <span className={`text-xs ${text.length > maxLength ? 'text-destructive' : 'text-muted-foreground'}`}>
              {text.length}/{maxLength}
            </span>
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share what you love about MockupPro..."
            className="min-h-[80px] resize-none"
            maxLength={maxLength + 10}
          />
        </div>
        
        {/* Submit button */}
        <Button 
          onClick={handleSubmit} 
          disabled={loading || !text.trim() || text.length > maxLength}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>
        
        <p className="text-xs text-muted-foreground text-center">
          Your testimonial will be reviewed before being published
        </p>
      </CardContent>
    </Card>
  );
};

export default TestimonialSection;
